import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useGmFiltersActionsWithTransition, useGmFiltersState } from '../../store/gmFiltersStore'
import FilterSection from './FilterSection'
import { CatergoryFilter } from './CatergoryFilter'
import { SidebarFiltersSkeleton } from '../skeleton'
import type { MenuFilterCategory } from '../../domain/menu-category-filter'
import type { MenuItemFilterSections, MenuItemFilter } from '../../domain/menu-item-filter'
import { FilterChip } from './filter-chip'

export const MenuFilters = ({
  filterCategories,
  loading,
  filterSections,
}: {
  filterCategories?: MenuFilterCategory[]
  filterSections?: MenuItemFilterSections
  loading: boolean
}) => {
  const { t } = useTranslation()
  const { goals, allergens, dietaries, selectedCategories, initialised } = useGmFiltersState()

  const {
    setSelectedCategories,
    setDietaryPreferences,
    clearFilters,
    initialiseFilters,
    isPending,
  } = useGmFiltersActionsWithTransition()

  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)

  useEffect(() => {
    if (initialised || !filterSections || !filterCategories) return

    const searchParams = new URLSearchParams(window.location.search)
    const categoryParam = searchParams.get('category')
    const goalsParam = searchParams.get('goals')
    const allergensParam = searchParams.get('allergens')
    const dietariesParam = searchParams.get('dietaries')
    const itemNameParam = searchParams.get('itemName')

    const categoryNames = categoryParam ? categoryParam.split(',').filter(Boolean) : []
    const matchedCategories =
      categoryNames.length > 0 ? filterCategories.filter((c) => categoryNames.includes(c.name)) : []

    const goalValues = goalsParam ? goalsParam.split(',').filter(Boolean) : []
    const allergenValues = allergensParam ? allergensParam.split(',').filter(Boolean) : []
    const dietaryValues = dietariesParam ? dietariesParam.split(',').filter(Boolean) : []

    const matchedGoals = goalValues
      .map((value) => filterSections.goals?.find((f) => f.value === value))
      .filter((f): f is MenuItemFilter => Boolean(f))

    const matchedDietaries = dietaryValues
      .map((value) => filterSections.dietaries?.find((f) => f.value === value))
      .filter((f): f is MenuItemFilter => Boolean(f))

    const matchedAllergens = allergenValues
      .map((value) => filterSections.allergens?.find((f) => f.value === value))
      .filter((f): f is MenuItemFilter => Boolean(f))

    initialiseFilters({
      itemName: itemNameParam || '',
      categories: matchedCategories,
      goals: matchedGoals,
      dietaries: matchedDietaries,
      allergens: matchedAllergens,
    })
  }, [initialised, filterSections, filterCategories, initialiseFilters])

  const hasActiveFilters = useMemo(
    () =>
      goals.length > 0 ||
      allergens.length > 0 ||
      dietaries.length > 0 ||
      selectedCategories.length > 0,
    [goals.length, allergens.length, dietaries.length, selectedCategories.length],
  )

  const handleRemoveFilter = useCallback(
    (type: 'goals' | 'allergens' | 'dietaries' | 'categories', filterValue: string) => {
      switch (type) {
        case 'goals':
          setDietaryPreferences(
            'goals',
            goals.filter((f) => f.value !== filterValue),
          )
          break
        case 'allergens':
          setDietaryPreferences(
            'allergens',
            allergens.filter((f) => f.value !== filterValue),
          )
          break
        case 'dietaries':
          setDietaryPreferences(
            'dietaries',
            dietaries.filter((f) => f.value !== filterValue),
          )
          break
        case 'categories':
          setSelectedCategories(
            selectedCategories.filter((c) =>
              c.name ? c.name !== filterValue : c.id !== filterValue,
            ) as MenuFilterCategory[],
          )
          break
      }
    },
    [goals, allergens, dietaries, selectedCategories, setDietaryPreferences, setSelectedCategories],
  )

  const getFilterLabel = useCallback(
    (filterValue: string, type: 'goals' | 'allergens' | 'dietaries' | 'categories') => {
      if (type === 'categories') {
        return filterValue
      }
      const translated = t(`allergenDietary:${filterValue}`)
      const negate = type === 'allergens' ? `${t('common:negate')} ` : ''
      return translated !== filterValue ? `${negate}${translated}` : filterValue
    },
    [t],
  )

  const isLoadingState = loading || !filterSections

  if (isLoadingState) {
    return <SidebarFiltersSkeleton />
  }

  if (!errorSnackbarOpen) {
    setErrorSnackbarOpen(true)
  }

  return (
    <Box padding={1}>
      <Stack spacing={2}>
        {hasActiveFilters && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'space-between',
                flexGrow: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Active Filters:
              </Typography>
              <Button
                variant="text"
                size="small"
                disabled={loading}
                onClick={() => {
                  clearFilters()
                }}
              >
                {t('marketplace:clearAll')}
              </Button>
            </Box>
            {selectedCategories.map((category) => (
              <FilterChip
                key={`category-${category.id}`}
                filter={category.name ? { value: category.name } : { value: category.id }}
                getFilterLabel={getFilterLabel}
                handleRemoveFilter={handleRemoveFilter}
                type="categories"
              />
            ))}
            {goals.map((filter) => (
              <FilterChip
                key={`goal-${filter.value}`}
                filter={filter}
                getFilterLabel={getFilterLabel}
                handleRemoveFilter={handleRemoveFilter}
                type="goals"
              />
            ))}

            {dietaries.map((filter) => (
              <FilterChip
                key={`dietary-${filter.value}`}
                filter={filter}
                getFilterLabel={getFilterLabel}
                handleRemoveFilter={handleRemoveFilter}
                type="dietaries"
              />
            ))}

            {allergens.map((filter) => (
              <FilterChip
                key={`allergen-${filter.value}`}
                filter={filter}
                getFilterLabel={getFilterLabel}
                handleRemoveFilter={handleRemoveFilter}
                type="allergens"
              />
            ))}
          </Box>
        )}

        {filterCategories && (
          <CatergoryFilter
            filterCategories={filterCategories}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
          />
        )}

        {filterSections?.goals && filterSections.goals.length > 0 && (
          <FilterSection
            type="goals"
            label={t('marketplace:goals')}
            filters={filterSections.goals}
            selectedFilters={goals}
            visibility={true}
            setDietaryPreferences={setDietaryPreferences}
          />
        )}

        {filterSections?.dietaries && filterSections.dietaries.length > 0 && (
          <FilterSection
            type="dietaries"
            label={t('marketplace:dietaries')}
            filters={filterSections.dietaries}
            selectedFilters={dietaries}
            visibility={true}
            setDietaryPreferences={setDietaryPreferences}
          />
        )}

        {filterSections?.allergens && filterSections.allergens.length > 0 && (
          <FilterSection
            type="allergens"
            label={t('marketplace:allergens')}
            filters={filterSections.allergens}
            selectedFilters={allergens}
            visibility={true}
            setDietaryPreferences={setDietaryPreferences}
          />
        )}
      </Stack>
    </Box>
  )
}

export default MenuFilters
